import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { eur, fmtDateTime, num } from "@/lib/format";
import { hasPerm } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, AlertTriangle, ArrowDownUp, Truck, ClipboardList, PackageCheck,
  Trash2, Boxes, ArrowDownToLine, FileText, Loader2, Sparkles,
} from "lucide-react";

// costo per unità base (fino a 4 decimali, per €/ml e €/g)
const perUnit = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(Number(n || 0));

const TYPE_LABEL = { consumable: "Consumabile", retail: "Retail", internal: "Interno" };

// uid stabile per le "key" React di righe editabili (append/remove) senza usare l'indice
let _uidSeq = 0;
const uid = () => `line-${++_uidSeq}`;
const MOVE_LABEL = {
  purchase: "Carico", consume_service: "Consumo servizio", sale_retail: "Vendita",
  return: "Reso", adjustment_in: "Rettifica +", adjustment_out: "Rettifica −",
  damaged: "Danneggiato", expired: "Scaduto", transfer_in: "Trasf. +", transfer_out: "Trasf. −",
};
const PO_STATUS = {
  draft: { label: "Bozza", variant: "secondary" }, sent: { label: "Inviato", variant: "default" },
  partial: { label: "Parziale", variant: "outline" }, received: { label: "Ricevuto", variant: "secondary" },
  cancelled: { label: "Annullato", variant: "destructive" },
};

export default function Prodotti() {
  const { user } = useAuth();
  const canCost = hasPerm(user, "warehouse.cost");
  const canWrite = hasPerm(user, "warehouse.write") || hasPerm(user, "products.write");
  const canConfirm = hasPerm(user, "inventory.confirm");

  const [tab, setTab] = useState("prodotti");
  const [products, setProducts] = useState([]);
  const [value, setValue] = useState(null);

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data));
    if (canCost) api.get("/stock/value").then((r) => setValue(r.data)).catch(() => {});
  }, [canCost]);
  useEffect(() => { load(); }, [load]);

  const lowCount = products.filter((p) => Number(p.stock) <= Number(p.min_stock)).length;

  return (
    <div data-testid="magazzino-page">
      <PageHeader title="Prodotti / Magazzino" subtitle="Inventario, movimenti, fornitori, ordini e costi">
        {canWrite && tab === "prodotti" && <NewProductButton onSaved={load} />}
      </PageHeader>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <KpiCard testid="kpi-products-count" label="Prodotti" value={num(products.length)} hint="referenze attive" />
        <KpiCard testid="kpi-low-stock" label="Sotto scorta" value={num(lowCount)} hint="da riordinare" />
        {canCost && <KpiCard testid="kpi-stock-value" label="Valore magazzino" value={eur(value?.stock_value_net || 0)} hint="a costo netto" />}
        {canCost && <KpiCard testid="kpi-retail-value" label="Valore retail" value={eur(value?.retail_value || 0)} hint="a prezzo vendita" />}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="prodotti" data-testid="tab-prodotti"><Boxes className="h-4 w-4 mr-1.5" />Prodotti</TabsTrigger>
          <TabsTrigger value="movimenti" data-testid="tab-movimenti"><ArrowDownUp className="h-4 w-4 mr-1.5" />Movimenti</TabsTrigger>
          <TabsTrigger value="fornitori" data-testid="tab-fornitori"><Truck className="h-4 w-4 mr-1.5" />Fornitori</TabsTrigger>
          <TabsTrigger value="ordini" data-testid="tab-ordini"><ClipboardList className="h-4 w-4 mr-1.5" />Ordini</TabsTrigger>
          <TabsTrigger value="inventario" data-testid="tab-inventario"><PackageCheck className="h-4 w-4 mr-1.5" />Inventario</TabsTrigger>
          {canCost && <TabsTrigger value="fatture" data-testid="tab-fatture"><FileText className="h-4 w-4 mr-1.5" />Fatture</TabsTrigger>}
        </TabsList>

        <TabsContent value="prodotti"><ProductsTab products={products} canCost={canCost} canWrite={canWrite} onChange={load} /></TabsContent>
        <TabsContent value="movimenti"><MovementsTab canCost={canCost} /></TabsContent>
        <TabsContent value="fornitori"><SuppliersTab canWrite={canWrite} /></TabsContent>
        <TabsContent value="ordini"><OrdersTab products={products} canWrite={canWrite} onChange={load} /></TabsContent>
        <TabsContent value="inventario"><InventoryTab canWrite={canWrite} canConfirm={canConfirm} onChange={load} /></TabsContent>
        {canCost && <TabsContent value="fatture"><InvoicesTab products={products} onChange={load} /></TabsContent>}
      </Tabs>
    </div>
  );
}

/* ------------------------- PRODOTTI ------------------------- */
const emptyProduct = {
  name: "", category: "", type: "retail", unit: "pz", package_size: 1,
  purchase_cost_net: 0, iva_pct: 22, price: 0, stock: 0, min_stock: 5,
  target_stock: 0, lead_time_days: 7, supplier_id: "", barcode: "", sku: "", location: "Magazzino",
};

function NewProductButton({ onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="product-new-button"><Plus className="h-4 w-4 mr-1" /> Nuovo prodotto</Button>
      {open && <ProductEditor product={null} onClose={() => setOpen(false)} onSaved={onSaved} />}
    </>
  );
}

function ProductsTab({ products, canCost, canWrite, onChange }) {
  const [edit, setEdit] = useState(null);
  const [moveFor, setMoveFor] = useState(null);
  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Prodotto</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead className="text-right">Giacenza</TableHead>
            {canCost && <TableHead className="text-right">Costo unit.</TableHead>}
            <TableHead className="text-right">Prezzo</TableHead>
            {canWrite && <TableHead className="text-right">Azioni</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((p) => {
            const low = Number(p.stock) <= Number(p.min_stock);
            return (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                <TableCell className="font-medium cursor-pointer" onClick={() => canWrite && setEdit(p)}>
                  {p.name}
                  <div className="text-[11px] text-muted-foreground">{p.sku} · {p.unit}{p.package_size > 1 ? ` · conf. ${p.package_size}` : ""}</div>
                </TableCell>
                <TableCell><Badge variant="outline">{TYPE_LABEL[p.type] || p.type}</Badge></TableCell>
                <TableCell>{p.category}</TableCell>
                <TableCell className="text-right tabular">
                  <Badge variant={low ? "destructive" : "secondary"} data-testid={`product-stock-${p.id}`}>
                    {low && <AlertTriangle className="h-3 w-3 mr-1" />}{num(p.stock)} {p.unit}
                  </Badge>
                </TableCell>
                {canCost && <TableCell className="text-right tabular text-muted-foreground">{perUnit(p.avg_unit_cost)}</TableCell>}
                <TableCell className="text-right tabular">{p.type === "consumable" ? "\u2014" : eur(p.price)}</TableCell>
                {canWrite && (
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setMoveFor(p)} data-testid={`product-move-${p.id}`}>
                      <ArrowDownUp className="h-4 w-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {products.length === 0 && (
            <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Nessun prodotto</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      {edit && <ProductEditor product={edit} onClose={() => setEdit(null)} onSaved={onChange} />}
      {moveFor && <MovementDialog product={moveFor} onClose={() => setMoveFor(null)} onSaved={onChange} canCost={canCost} />}
    </Card>
  );
}

function ProductEditor({ product, onClose, onSaved }) {
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ ...emptyProduct, ...(product || {}) });
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name) { toast.error("Nome obbligatorio"); return; }
    const payload = { ...form, supplier_id: form.supplier_id || null };
    try {
      if (product) await api.put(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Prodotto salvato"); onClose(); onSaved();
    } catch { toast.error("Errore nel salvataggio"); }
  };
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader><SheetTitle className="font-heading text-2xl">{product ? "Modifica prodotto" : "Nuovo prodotto"}</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-6">
          <div className="space-y-1.5"><Label>Nome</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Categoria</Label><Input value={form.category} onChange={(e) => set("category", e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="product-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="retail">Retail (vendibile)</SelectItem>
                  <SelectItem value="consumable">Consumabile tecnico</SelectItem>
                  <SelectItem value="internal">Materiale interno</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Unità</Label>
              <Select value={form.unit} onValueChange={(v) => set("unit", v)}>
                <SelectTrigger data-testid="product-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pz">pz</SelectItem>
                  <SelectItem value="ml">ml</SelectItem>
                  <SelectItem value="g">g</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Qtà confezione ({form.unit})</Label><Input type="number" value={form.package_size} onChange={(e) => set("package_size", +e.target.value)} data-testid="product-package-size" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Costo confezione netto (€)</Label><Input type="number" step="0.01" value={form.purchase_cost_net} onChange={(e) => set("purchase_cost_net", +e.target.value)} data-testid="product-cost" /></div>
            <div className="space-y-1.5"><Label>IVA %</Label><Input type="number" value={form.iva_pct} onChange={(e) => set("iva_pct", +e.target.value)} /></div>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-2">Costo unitario stimato: {perUnit((form.purchase_cost_net || 0) / (form.package_size || 1))}/{form.unit}</p>
          {form.type !== "consumable" && (
            <div className="space-y-1.5"><Label>Prezzo retail (€)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => set("price", +e.target.value)} data-testid="product-price" /></div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Giacenza ({form.unit})</Label><Input type="number" value={form.stock} onChange={(e) => set("stock", +e.target.value)} data-testid="product-stock" disabled={!!product} title={product ? "Usa i Movimenti per variare la giacenza" : ""} /></div>
            <div className="space-y-1.5"><Label>Scorta min.</Label><Input type="number" value={form.min_stock} onChange={(e) => set("min_stock", +e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Scorta target</Label><Input type="number" value={form.target_stock} onChange={(e) => set("target_stock", +e.target.value)} /></div>
          </div>
          {product && <p className="text-[11px] text-muted-foreground -mt-2">La giacenza si modifica solo tramite Movimenti/Ordini/Inventario (tracciabilità).</p>}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Lead time (gg)</Label><Input type="number" value={form.lead_time_days} onChange={(e) => set("lead_time_days", +e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Fornitore</Label>
              <Select value={form.supplier_id || "none"} onValueChange={(v) => set("supplier_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="product-supplier"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nessuno —</SelectItem>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>SKU</Label><Input value={form.sku || ""} onChange={(e) => set("sku", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Barcode</Label><Input value={form.barcode || ""} onChange={(e) => set("barcode", e.target.value)} /></div>
          </div>
        </div>
        <SheetFooter className="mt-6"><Button className="w-full" onClick={save} data-testid="product-save">Salva</Button></SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function MovementDialog({ product, onClose, onSaved, canCost }) {
  const [type, setType] = useState("purchase");
  const [qty, setQty] = useState(0);
  const [unitCost, setUnitCost] = useState(product.avg_unit_cost || 0);
  const [reason, setReason] = useState("");
  const save = async () => {
    if (!qty || qty <= 0) { toast.error("Quantità non valida"); return; }
    try {
      await api.post(`/products/${product.id}/movement`, {
        type, qty: +qty, unit_cost: type === "purchase" ? +unitCost : undefined, reason,
      });
      toast.success("Movimento registrato"); onClose(); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="movement-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Movimento · {product.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Tipo movimento</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="movement-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="purchase">Carico (acquisto)</SelectItem>
                <SelectItem value="adjustment_in">Rettifica +</SelectItem>
                <SelectItem value="adjustment_out">Rettifica −</SelectItem>
                <SelectItem value="damaged">Danneggiato</SelectItem>
                <SelectItem value="expired">Scaduto</SelectItem>
                <SelectItem value="return">Reso (rientro)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Quantità ({product.unit})</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="movement-qty" /></div>
            {type === "purchase" && canCost && (
              <div className="space-y-1.5"><Label>Costo unit. (€/{product.unit})</Label><Input type="number" step="0.0001" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} data-testid="movement-cost" /></div>
            )}
          </div>
          <div className="space-y-1.5"><Label>Causale / nota</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="movement-reason" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="movement-save">Registra movimento</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------- MOVIMENTI ------------------------- */
function MovementsTab({ canCost }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/stock/movements").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Data</TableHead><TableHead>Prodotto</TableHead><TableHead>Tipo</TableHead>
          <TableHead className="text-right">Qtà</TableHead>{canCost && <TableHead className="text-right">Costo unit.</TableHead>}
          <TableHead>Causale</TableHead><TableHead>Utente</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((m) => (
            <TableRow key={m.id} data-testid={`movement-row-${m.id}`}>
              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(m.created_at)}</TableCell>
              <TableCell className="font-medium">{m.product_name}</TableCell>
              <TableCell><Badge variant="outline">{MOVE_LABEL[m.type] || m.type}</Badge></TableCell>
              <TableCell className={`text-right tabular font-medium ${m.qty < 0 ? "text-destructive" : "text-[hsl(var(--hi-status-success))]"}`}>{m.qty > 0 ? "+" : ""}{num(m.qty)} {m.unit}</TableCell>
              {canCost && <TableCell className="text-right tabular text-muted-foreground">{perUnit(m.unit_cost)}</TableCell>}
              <TableCell className="text-sm">{m.reason}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{m.user_name || "—"}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Nessun movimento</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

/* ------------------------- FORNITORI ------------------------- */
function SuppliersTab({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/suppliers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const empty = { name: "", contact: "", email: "", phone: "", lead_time_days: 7, conditions: "", notes: "" };
  const [form, setForm] = useState(empty);
  const openEd = (s) => { setEdit(s); setForm(s ? { ...empty, ...s } : empty); setOpen(true); };
  const save = async () => {
    if (!form.name) { toast.error("Nome obbligatorio"); return; }
    if (edit) await api.put(`/suppliers/${edit.id}`, form); else await api.post("/suppliers", form);
    toast.success("Fornitore salvato"); setOpen(false); load();
  };
  const del = async (s) => { await api.delete(`/suppliers/${s.id}`); toast.success("Eliminato"); load(); };
  return (
    <Card className="p-0 overflow-hidden">
      {canWrite && <div className="p-3 border-b border-border flex justify-end"><Button size="sm" onClick={() => openEd(null)} data-testid="supplier-new-button"><Plus className="h-4 w-4 mr-1" /> Nuovo fornitore</Button></div>}
      <Table>
        <TableHeader><TableRow><TableHead>Fornitore</TableHead><TableHead>Referente</TableHead><TableHead>Contatti</TableHead><TableHead className="text-right">Lead time</TableHead>{canWrite && <TableHead></TableHead>}</TableRow></TableHeader>
        <TableBody>
          {rows.map((s) => (
            <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
              <TableCell className="font-medium cursor-pointer" onClick={() => canWrite && openEd(s)}>{s.name}<div className="text-[11px] text-muted-foreground">{s.conditions}</div></TableCell>
              <TableCell>{s.contact || "—"}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{s.email}<br />{s.phone}</TableCell>
              <TableCell className="text-right tabular">{s.lead_time_days} gg</TableCell>
              {canWrite && <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => del(s)} data-testid={`supplier-del-${s.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>}
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Nessun fornitore</TableCell></TableRow>}
        </TableBody>
      </Table>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading text-2xl">{edit ? "Modifica fornitore" : "Nuovo fornitore"}</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-1.5"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" /></div>
            <div className="space-y-1.5"><Label>Referente</Label><Input value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Telefono</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Lead time (gg)</Label><Input type="number" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: +e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Condizioni</Label><Input value={form.conditions || ""} onChange={(e) => setForm({ ...form, conditions: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Note</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <Button className="w-full" onClick={save} data-testid="supplier-save">Salva</Button>
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  );
}

/* ------------------------- ORDINI ------------------------- */
function OrdersTab({ products, canWrite, onChange }) {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = () => api.get("/purchase-orders").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);
  const receive = async (o) => {
    try { const { data } = await api.post(`/purchase-orders/${o.id}/receive`, {}); toast.success(`Ricevuto (${data.movements} carichi)`); load(); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <Card className="p-0 overflow-hidden">
      {canWrite && <div className="p-3 border-b border-border flex justify-end"><Button size="sm" onClick={() => setCreating(true)} data-testid="po-new-button"><Plus className="h-4 w-4 mr-1" /> Nuovo ordine</Button></div>}
      <Table>
        <TableHeader><TableRow><TableHead>Fornitore</TableHead><TableHead>Righe</TableHead><TableHead className="text-right">Totale</TableHead><TableHead>Stato</TableHead>{canWrite && <TableHead className="text-right">Azioni</TableHead>}</TableRow></TableHeader>
        <TableBody>
          {rows.map((o) => {
            const st = PO_STATUS[o.status] || { label: o.status, variant: "outline" };
            return (
              <TableRow key={o.id} data-testid={`po-row-${o.id}`}>
                <TableCell className="font-medium">{o.supplier_name}<div className="text-[11px] text-muted-foreground">{fmtDateTime(o.created_at)}</div></TableCell>
                <TableCell className="text-sm">{o.lines?.length} prodotti</TableCell>
                <TableCell className="text-right tabular">{eur(o.total_cost)}</TableCell>
                <TableCell><Badge variant={st.variant}>{st.label}</Badge></TableCell>
                {canWrite && (
                  <TableCell className="text-right">
                    {["draft", "sent", "partial"].includes(o.status) && (
                      <Button variant="outline" size="sm" onClick={() => receive(o)} data-testid={`po-receive-${o.id}`}>
                        <ArrowDownToLine className="h-4 w-4 mr-1" /> Ricevi
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Nessun ordine</TableCell></TableRow>}
        </TableBody>
      </Table>
      {creating && <OrderCreator products={products} suppliers={suppliers} onClose={() => setCreating(false)} onSaved={() => { load(); onChange(); }} />}
    </Card>
  );
}

function OrderCreator({ products, suppliers, onClose, onSaved }) {
  const [supplierId, setSupplierId] = useState("");
  const [lines, setLines] = useState([]);
  const addLine = () => setLines((l) => [...l, { product_id: "", qty: 1, unit_cost: 0, _uid: uid() }]);
  const setLine = (i, k, v) => setLines((l) => l.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const removeLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));
  const onPickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    setLines((l) => l.map((x, idx) => idx === i ? { ...x, product_id: pid, unit_cost: p?.purchase_cost_net || 0 } : x));
  };
  const total = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_cost || 0), 0);
  const save = async (status) => {
    const valid = lines.filter((l) => l.product_id && l.qty > 0);
    if (!supplierId) { toast.error("Seleziona un fornitore"); return; }
    if (valid.length === 0) { toast.error("Aggiungi almeno una riga"); return; }
    try {
      await api.post("/purchase-orders", { supplier_id: supplierId, status, lines: valid });
      toast.success("Ordine creato"); onClose(); onSaved();
    } catch { toast.error("Errore"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="po-creator">
        <DialogHeader><DialogTitle className="font-heading text-xl">Nuovo ordine d'acquisto</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Fornitore</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="po-supplier"><SelectValue placeholder="Seleziona fornitore" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between"><Label>Righe ordine</Label><Button variant="outline" size="sm" onClick={addLine} data-testid="po-add-line"><Plus className="h-4 w-4 mr-1" /> Riga</Button></div>
            {lines.map((l, i) => (
              <div key={l._uid} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-6">
                  <Select value={l.product_id} onValueChange={(v) => onPickProduct(i, v)}>
                    <SelectTrigger data-testid={`po-line-product-${i}`}><SelectValue placeholder="Prodotto" /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Input type="number" value={l.qty} onChange={(e) => setLine(i, "qty", +e.target.value)} placeholder="Conf." data-testid={`po-line-qty-${i}`} /></div>
                <div className="col-span-3"><Input type="number" step="0.01" value={l.unit_cost} onChange={(e) => setLine(i, "unit_cost", +e.target.value)} placeholder="€/conf." /></div>
                <div className="col-span-1"><Button variant="ghost" size="icon" onClick={() => removeLine(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button></div>
              </div>
            ))}
            {lines.length === 0 && <p className="text-sm text-muted-foreground">Nessuna riga. Aggiungi prodotti da ordinare.</p>}
          </div>
          <div className="text-right font-heading text-lg">Totale: {eur(total)}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => save("draft")} data-testid="po-save-draft">Salva bozza</Button>
          <Button onClick={() => save("sent")} data-testid="po-save-sent">Crea e invia</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------- INVENTARIO ------------------------- */
function InventoryTab({ canWrite, canConfirm, onChange }) {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const load = () => api.get("/inventory/counts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const startNew = async () => {
    try { const { data } = await api.post("/inventory/counts", {}); setActive(data); toast.success("Inventario avviato"); load(); }
    catch { toast.error("Errore"); }
  };
  return (
    <Card className="p-0 overflow-hidden">
      {canWrite && <div className="p-3 border-b border-border flex justify-end"><Button size="sm" onClick={startNew} data-testid="inventory-new-button"><Plus className="h-4 w-4 mr-1" /> Nuovo inventario</Button></div>}
      <Table>
        <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Righe</TableHead><TableHead>Stato</TableHead><TableHead>Operatore</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((c) => (
            <TableRow key={c.id} data-testid={`inventory-row-${c.id}`}>
              <TableCell className="text-sm">{fmtDateTime(c.created_at)}</TableCell>
              <TableCell>{c.lines?.length} prodotti</TableCell>
              <TableCell><Badge variant={c.status === "confirmed" ? "secondary" : "default"}>{c.status === "confirmed" ? "Confermato" : "Aperto"}</Badge></TableCell>
              <TableCell className="text-sm text-muted-foreground">{c.user_name}</TableCell>
              <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => setActive(c)} data-testid={`inventory-open-${c.id}`}>{c.status === "confirmed" ? "Dettagli" : "Compila"}</Button></TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Nessun inventario</TableCell></TableRow>}
        </TableBody>
      </Table>
      {active && <InventorySheet count={active} canConfirm={canConfirm} onClose={() => setActive(null)} onSaved={() => { load(); onChange(); }} />}
    </Card>
  );
}

function InventorySheet({ count, canConfirm, onClose, onSaved }) {
  const [lines, setLines] = useState(count.lines.map((l) => ({ ...l })));
  const readonly = count.status === "confirmed";
  const setCounted = (id, v) => setLines((ls) => ls.map((l) => l.id === id ? { ...l, counted_qty: v === "" ? null : +v } : l));
  const saveDraft = async () => {
    const counts = lines.filter((l) => l.counted_qty !== null && l.counted_qty !== undefined).map((l) => ({ line_id: l.id, counted_qty: l.counted_qty }));
    await api.put(`/inventory/counts/${count.id}`, { counts }); toast.success("Salvato"); onSaved();
  };
  const confirm = async () => {
    await saveDraft();
    try { const { data } = await api.post(`/inventory/counts/${count.id}/confirm`); toast.success(`Confermato · ${data.adjustments} rettifiche`); onClose(); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle className="font-heading text-2xl">Inventario fisico</SheetTitle></SheetHeader>
        <div className="space-y-2 mt-6">
          {lines.map((l) => {
            const diff = (l.counted_qty ?? null) === null ? null : l.counted_qty - l.theoretical_qty;
            return (
              <div key={l.id} className="flex items-center gap-2 py-1.5 border-b border-border/60" data-testid={`inv-line-${l.id}`}>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{l.product_name}</div><div className="text-[11px] text-muted-foreground">Teorico: {num(l.theoretical_qty)} {l.unit}</div></div>
                {readonly ? (
                  <div className="text-sm tabular w-24 text-right">{l.counted_qty ?? "—"} {l.unit}</div>
                ) : (
                  <Input type="number" className="w-24" placeholder="Contato" value={l.counted_qty ?? ""} onChange={(e) => setCounted(l.id, e.target.value)} data-testid={`inv-input-${l.id}`} />
                )}
                {diff !== null && Math.abs(diff) > 0.0001 && (
                  <Badge variant={diff < 0 ? "destructive" : "secondary"} className="w-16 justify-center">{diff > 0 ? "+" : ""}{num(diff)}</Badge>
                )}
              </div>
            );
          })}
        </div>
        {!readonly && (
          <SheetFooter className="mt-6 flex-col gap-2">
            <Button variant="outline" className="w-full" onClick={saveDraft} data-testid="inventory-save-draft">Salva conteggio</Button>
            {canConfirm && <Button className="w-full" onClick={confirm} data-testid="inventory-confirm"><PackageCheck className="h-4 w-4 mr-1" /> Conferma e genera rettifiche</Button>}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}


/* ------------------------- FATTURE (AI OCR) ------------------------- */
function InvoicesTab({ products, onChange }) {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(null);
  const load = () => api.get("/invoices").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/invoices/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      toast.success("Fattura letta dall'AI"); load(); setActive(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore lettura fattura"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <Card className="p-0 overflow-hidden">
      <div className="p-3 border-b border-border flex items-center justify-between">
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[hsl(var(--hi-gold))]" /> Carica una fattura (PDF/foto): l'AI compila i dati, tu confermi prima del carico magazzino.</p>
        <Button asChild size="sm" disabled={uploading} data-testid="invoice-upload-button">
          <label className="cursor-pointer">
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            {uploading ? "Lettura AI..." : "Carica fattura"}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid="invoice-input" />
          </label>
        </Button>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Fornitore</TableHead><TableHead>Numero</TableHead><TableHead>Data</TableHead><TableHead className="text-right">Totale</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((inv) => (
            <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
              <TableCell className="font-medium">{inv.parsed?.supplier_name || "\u2014"}{inv.is_duplicate && <Badge variant="destructive" className="ml-2">Possibile duplicato</Badge>}</TableCell>
              <TableCell>{inv.parsed?.invoice_number || "\u2014"}</TableCell>
              <TableCell className="text-sm">{inv.parsed?.invoice_date || "\u2014"}</TableCell>
              <TableCell className="text-right tabular">{eur(inv.parsed?.total || 0)}</TableCell>
              <TableCell><Badge variant={inv.status === "confirmed" ? "secondary" : "default"}>{inv.status === "confirmed" ? "Confermata" : "Bozza"}</Badge></TableCell>
              <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => setActive(inv)} data-testid={`invoice-open-${inv.id}`}>{inv.status === "confirmed" ? "Dettagli" : "Verifica"}</Button></TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nessuna fattura caricata</TableCell></TableRow>}
        </TableBody>
      </Table>
      {active && <InvoiceReview invoice={active} products={products} suppliers={suppliers} onClose={() => setActive(null)} onSaved={() => { load(); onChange(); }} />}
    </Card>
  );
}

function InvoiceReview({ invoice, products, suppliers, onClose, onSaved }) {
  const readonly = invoice.status === "confirmed";
  const [supplierId, setSupplierId] = useState(invoice.supplier_id || "");
  const [lines, setLines] = useState((invoice.parsed?.lines || []).map((l, i) => ({ ...l, _uid: l.id || `inv-${i}` })));
  const setLine = (i, k, v) => setLines((ls) => ls.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const confirm = async () => {
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/confirm`, { supplier_id: supplierId || null, lines });
      toast.success(`Confermata · ${data.movements_created} carichi a magazzino`); onClose(); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="invoice-review">
        <DialogHeader><DialogTitle className="font-heading text-xl">{invoice.parsed?.supplier_name} · Fattura {invoice.parsed?.invoice_number}</DialogTitle></DialogHeader>
        {invoice.is_duplicate && <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">Attenzione: numero fattura già presente tra le confermate.</div>}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div><span className="text-muted-foreground">Data</span><div className="font-medium">{invoice.parsed?.invoice_date || "\u2014"}</div></div>
          <div><span className="text-muted-foreground">Imponibile</span><div className="font-medium">{eur(invoice.parsed?.subtotal_net || 0)}</div></div>
          <div><span className="text-muted-foreground">Totale</span><div className="font-medium">{eur(invoice.parsed?.total || 0)}</div></div>
        </div>
        <div className="space-y-1.5">
          <Label>Fornitore</Label>
          <Select value={supplierId} onValueChange={setSupplierId} disabled={readonly}>
            <SelectTrigger data-testid="invoice-supplier"><SelectValue placeholder="Associa fornitore" /></SelectTrigger>
            <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="max-h-[42vh] overflow-y-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Descrizione fattura</TableHead><TableHead>Prodotto (magazzino)</TableHead><TableHead className="text-right">Q.tà</TableHead><TableHead className="text-right">€/conf.</TableHead><TableHead className="text-center">Carica</TableHead></TableRow></TableHeader>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={l._uid} data-testid={`invoice-line-${i}`}>
                  <TableCell className="text-sm">{l.description}</TableCell>
                  <TableCell>
                    <Select value={l.product_id || "none"} onValueChange={(v) => setLine(i, "product_id", v === "none" ? null : v)} disabled={readonly}>
                      <SelectTrigger className="h-8 text-xs min-w-[180px]" data-testid={`invoice-line-product-${i}`}><SelectValue placeholder="— non a magazzino —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— non a magazzino (solo costo) —</SelectItem>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right"><Input type="number" className="h-8 w-20 text-xs ml-auto" value={l.quantity ?? 0} onChange={(e) => setLine(i, "quantity", +e.target.value)} disabled={readonly} /></TableCell>
                  <TableCell className="text-right"><Input type="number" step="0.01" className="h-8 w-24 text-xs ml-auto" value={l.unit_price_net ?? 0} onChange={(e) => setLine(i, "unit_price_net", +e.target.value)} disabled={readonly} /></TableCell>
                  <TableCell className="text-center"><input type="checkbox" checked={!!l.load_stock} onChange={(e) => setLine(i, "load_stock", e.target.checked)} disabled={readonly || !l.product_id} data-testid={`invoice-line-load-${i}`} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!readonly && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={confirm} data-testid="invoice-confirm"><ArrowDownToLine className="h-4 w-4 mr-1" /> Conferma e carica magazzino</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
